import React, { useState, useEffect } from 'react';
import { Wand2, Loader2, Image as ImageIcon, CheckCircle, XCircle, Download, FileText } from 'lucide-react';
import apiService from '../services/apiService';

const UAD36Form = ({ subjectProperty, selectedComparables, marketData, onUpdate }) => {
  // Accordion state for collapsible sections
  const [expandedSections, setExpandedSections] = useState({
    subject: true,
    site: true,
    improvements: true,
    rooms: true,
    amenities: true,
    comparables: true,
    market: true,
    valuation: true,
    images: true  // New section for images
  });

  // State for image analysis
  const [propertyImages, setPropertyImages] = useState([]);
  const [isAnalyzingImages, setIsAnalyzingImages] = useState(false);
  const [analysisResult, setAnalysisResult] = useState(null);
  const [analysisError, setAnalysisError] = useState(null);

  // State for PDF generation
  const [isGeneratingPDF, setIsGeneratingPDF] = useState(false);
  const [pdfError, setPdfError] = useState(null);
  const [additionalFiles, setAdditionalFiles] = useState([]);

  const toggleSection = (section) => {
    setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));
  };

  const [formData, setFormData] = useState({
    // All fields from 2.6 plus 3.6 enhancements
    propertyAddress: '',
    city: '',
    state: '',
    zip: '',
    legalDescription: '',
    apn: '',
    taxYear: new Date().getFullYear(),
    reTaxes: '',
    neighborhoodName: '',
    mapReference: '',
    censusTract: '',
    occupant: 'Owner',
    specialAssessments: '',
    pud: 'No',
    hoa: 'No',
    hoaPerMonth: '',

    // CONTRACT
    salePrice: '',
    dateOfSale: '',
    concessions: '',
    propertyRights: 'FeeSimple',
    assignmentType: 'Purchase',

    // NEIGHBORHOOD
    location: 'Suburban',
    builtUp: '25-75%',
    growth: 'Stable',
    propertyValues: 'Stable',
    demandSupply: 'In Balance',
    marketingTime: '3-6 months',
    neighborhoodBoundaries: '',
    neighborhoodDescription: '',
    marketConditions: '',
    marketTrend: 'Stable',
    locationType: 'Suburban',

    // SITE
    siteDimensions: '',
    siteArea: '',
    siteShape: 'Rectangular',
    siteTopography: 'Level',
    view: 'Residential',
    viewType: 'Residential',
    zoningClassification: '',
    zoningDescription: '',
    zoningCompliance: 'Legal',
    utilityElectricity: 'Public',
    utilityGas: 'Public',
    utilityWater: 'Public',
    utilitySewer: 'Public',
    waterSource: 'PublicSupply',
    sewerSystem: 'PublicSewer',
    femaFloodHazard: 'No',
    femaZone: '',
    femaMapNumber: '',
    femaMapDate: '',

    // IMPROVEMENTS - General (UAD 3.6 Enhanced)
    units: 'One',
    unitCount: '1',
    numStories: '1',
    levelCount: '1',
    type: 'Det',
    attachmentType: 'Detached',
    design: 'Ranch',
    yearBuilt: '',
    effectiveAge: '',
    constructionMethod: 'SiteBuilt',
    constructionStatus: 'Complete',
    propertySubType: 'SingleFamilyResidence',
    currentUsage: 'SingleFamilyResidence',
    propertyEstate: 'FeeSimple',
    foundation: 'Slab',
    foundationType: 'Slab',
    exteriorWalls: 'Brick',
    exteriorMaterial: 'Brick',
    roofSurface: 'Asphalt',
    roofMaterial: 'Composition',
    guttersDownspouts: 'Yes',
    windowType: 'Vinyl',
    stormSash: 'Yes',
    screens: 'Yes',

    // UAD 3.6 Quality Ratings (Q1-Q6) - Empty until AI analysis
    overallQuality: '',
    interiorQuality: '',
    exteriorQuality: '',

    // UAD 3.6 Condition Ratings (C1-C6) - Empty until AI analysis
    overallCondition: '',
    interiorCondition: '',
    exteriorCondition: '',

    // Basement
    basementArea: '0',
    basementFinishPercent: '0',
    basementOutsideEntry: 'No',
    basementRecRoom: '0',
    basementBedroom: '0',
    basementBath: '0',

    // Insulation
    insulationRoof: 'Yes',
    insulationCeiling: 'Yes',
    insulationWalls: 'Yes',
    insulationFloor: 'No',

    // Heating/Cooling (UAD 3.6 Enhanced)
    heating: 'FWA',
    heatingSystem: 'ForcedWarmAir',
    heatingFuel: 'Gas',
    heatingFuelType: 'NaturalGas',
    cooling: 'Central',
    coolingSystem: 'Central',

    // Amenities
    fireplaces: '0',
    patioDeck: 'Yes',
    pool: 'No',
    fence: 'Wood',
    porch: 'Yes',

    // Car Storage (UAD 3.6 Enhanced)
    carStorageType: 'Garage',
    carStorageTypeUAD: 'AttachedGarage',
    garageSpaces: '2',
    garageType: 'Att',

    // ROOM LIST
    foyer: '0',
    livingRoom: '1',
    diningRoom: '1',
    kitchen: '1',
    den: '0',
    familyRoom: '1',
    level1Bedrooms: '0',
    level1Baths: '1',
    laundry: '1',
    level2Bedrooms: '4',
    level2Baths: '2',
    gla: '',
    totalBedrooms: '4',
    totalBathrooms: '3',
    fullBathrooms: '3',
    halfBathrooms: '0',
    threeQuarterBathrooms: '0',

    // INTERIOR
    floors: 'Hardwood',
    walls: 'Drywall',
    trim: 'Wood',
    bathFloor: 'Ceramic',
    bathWainscot: 'Ceramic',
    doors: 'Wood',
    attic: 'Stairs',
    appliances: 'Refrigerator,Range,Dishwasher,Disposal,Microwave',

    // CONDITION (legacy)
    condition: 'Average',
    quality: 'Average',

    // UAD 3.6 Valuation Method
    valuationMethod: 'TraditionalAppraisal',

    // APPRAISER
    appraiserName: '',
    appraiserSignature: '',
    stateCertification: '',
    appraiserState: '',
    effectiveDate: new Date().toISOString().split('T')[0],
    reportDate: new Date().toISOString().split('T')[0],

    // RECONCILIATION
    indicatedValueSCA: '',
    indicatedValueIncome: '',
    indicatedValueCost: '',
    finalValue: '',
    reconciliationComments: ''
  });

  // Extract property images from subjectProperty
  useEffect(() => {
    if (subjectProperty) {
      // PropMix provides images in ImageURLs or Media array
      const images = subjectProperty.ImageURLs || subjectProperty.Media || [];
      setPropertyImages(images);
      console.log(`📸 Found ${images.length} property images`);
    }
  }, [subjectProperty]);

  useEffect(() => {
    if (subjectProperty) {
      console.log('UAD36 Form - Subject Property Data:', subjectProperty);
      const effectiveAge = subjectProperty.YearBuilt ? (new Date().getFullYear() - parseInt(subjectProperty.YearBuilt)).toString() : '';

      setFormData(prev => ({
        ...prev,
        // SUBJECT
        propertyAddress: subjectProperty.StreetAddress || subjectProperty.UnparsedAddress || subjectProperty.Address || '',
        city: subjectProperty.City || '',
        state: subjectProperty.StateOrProvince || '',
        zip: subjectProperty.PostalCode || '',
        apn: subjectProperty.ParcelNumber || '',
        taxYear: subjectProperty.AssessedYear || new Date().getFullYear(),
        reTaxes: subjectProperty.AssessedValue || subjectProperty.TaxAmount || '',
        neighborhoodName: subjectProperty.SubDivisionName || '',
        censusTract: subjectProperty.CensusTractId || '',

        // CONTRACT
        salePrice: subjectProperty.ListPrice || subjectProperty.ClosePrice || subjectProperty.SaleAmount || '',
        dateOfSale: subjectProperty.CloseDate || subjectProperty.ListingContractDate || '',

        // SITE
        siteArea: subjectProperty.LotSizeSquareFeet || '',
        siteDimensions: subjectProperty.LotSizeDimensions || '',
        zoningClassification: subjectProperty.Zoning || '',
        zoningDescription: subjectProperty.ZoningDescription || '',

        // IMPROVEMENTS - General
        yearBuilt: subjectProperty.YearBuilt || '',
        effectiveAge: effectiveAge,
        numStories: subjectProperty.StoriesTotal || subjectProperty.DerivedStories || subjectProperty.Stories || '1',
        levelCount: subjectProperty.StoriesTotal || subjectProperty.DerivedStories || subjectProperty.Stories || '1',
        design: subjectProperty.ArchitecturalStyle || 'Traditional',
        exteriorWalls: subjectProperty.ConstructionMaterials?.split(',')[0] || 'Brick',
        roofSurface: subjectProperty.Roof?.includes('Composition') ? 'Asphalt' : (subjectProperty.Roof || 'Asphalt'),

        // Heating/Cooling (UAD 3.6 specific enums)
        heating: subjectProperty.Heating?.includes('Central') ? 'CentralFurnace' : 'Other',
        heatingFuel: subjectProperty.Heating?.includes('Natural Gas') || subjectProperty.Heating?.includes('Gas') ? 'NaturalGas' : 'Electric',
        cooling: subjectProperty.Cooling?.includes('Central') ? 'CentralAir' : 'None',

        // Amenities
        fireplaces: subjectProperty.FireplacesTotal || '0',
        pool: (subjectProperty.PoolFeatures && subjectProperty.PoolFeatures !== '') || subjectProperty.PoolType ? 'Yes' : 'No',

        // Car Storage
        carStorageType: subjectProperty.CarStorageType?.includes('Attached') ? 'AttachedGarage' :
                        subjectProperty.CarStorageType?.includes('Garage') ? 'DetachedGarage' :
                        subjectProperty.GarageYN === 'Y' ? 'AttachedGarage' : 'None',
        garageSpaces: subjectProperty.GarageSpaces || subjectProperty.ParkingSpaces || '2',

        // ROOM LIST & Square Footage
        totalBedrooms: subjectProperty.BedroomsTotal || '',
        totalBathrooms: subjectProperty.BathroomsTotalInteger || subjectProperty.BathroomsDecimal || '',
        fullBathrooms: subjectProperty.BathroomsFull || subjectProperty.BathroomsFullCalculated || Math.floor(parseFloat(subjectProperty.BathroomsDecimal || 0)),
        halfBathrooms: subjectProperty.BathroomsHalf || subjectProperty.BathroomsHalfCalculated || Math.round((parseFloat(subjectProperty.BathroomsDecimal || 0) % 1) * 2),
        threeQuarterBathrooms: '0',
        gla: subjectProperty.LivingArea || '',
        basementArea: subjectProperty.BelowGradeTotalArea || subjectProperty.BasementArea || '0',
        garageArea: subjectProperty.GarageArea || '',

        // INTERIOR
        appliances: subjectProperty.Appliances || 'Refrigerator,Range,Dishwasher,Disposal,Microwave',

        // UAD 3.6 Specific
        waterSource: 'PublicSupply',
        sewerSystem: 'PublicSewer',
        constructionMethod: 'SiteBuilt',
        propertyEstate: 'FeeSimple',

        // CONDITION (UAD 3.6 requires Q/C ratings - filled by AI analysis)
        overallQuality: '',
        interiorQuality: '',
        exteriorQuality: '',
        overallCondition: '',
        interiorCondition: '',
        exteriorCondition: ''
      }));
    }
  }, [subjectProperty]);

  // Analyze property images with OpenAI Vision for UAD 3.6 ratings
  const analyzePropertyImages = async () => {
    if (!propertyImages || propertyImages.length === 0) {
      setAnalysisError('No property images available to analyze');
      return;
    }

    setIsAnalyzingImages(true);
    setAnalysisError(null);

    try {
      console.log(`🔍 Analyzing ${propertyImages.length} images for UAD 3.6 ratings...`);

      // Use longer timeout for AI image analysis (5 minutes)
      const response = await apiService.post('/vision/analyze-uad36-images', {
        imageUrls: propertyImages,
        propertyAddress: formData.propertyAddress
      }, {
        timeout: 300000  // 5 minutes for AI analysis
      });

      if (response.data.success) {
        const result = response.data.data;
        setAnalysisResult(result);

        // Auto-populate C/Q ratings
        const ratings = result.overallRatings;
        setFormData(prev => ({
          ...prev,
          exteriorCondition: ratings.exteriorCondition,
          exteriorQuality: ratings.exteriorQuality,
          interiorCondition: ratings.interiorCondition,
          interiorQuality: ratings.interiorQuality,
          overallCondition: ratings.overallCondition,
          overallQuality: ratings.overallQuality,
          reconciliationComments: ratings.reconciliationNotes
        }));

        console.log('✅ Image analysis complete and ratings applied');
      }
    } catch (error) {
      console.error('❌ Image analysis error:', error);
      setAnalysisError(error.response?.data?.details || error.message || 'Failed to analyze images');
    } finally {
      setIsAnalyzingImages(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (onUpdate) onUpdate({ ...formData, [field]: value });
  };

  // Generate and download PDF
  const generatePDF = async () => {
    setIsGeneratingPDF(true);
    setPdfError(null);

    try {
      console.log('📄 Generating UAD 3.6 PDF report...');

      // Check if there are additional files to upload
      if (additionalFiles.length > 0) {
        // Use multipart/form-data for file uploads
        const formDataObj = new FormData();
        formDataObj.append('formData', JSON.stringify(formData));
        formDataObj.append('subjectProperty', JSON.stringify(subjectProperty));
        formDataObj.append('comparables', JSON.stringify(selectedComparables));
        formDataObj.append('marketData', JSON.stringify(marketData));
        formDataObj.append('analysisResult', JSON.stringify(analysisResult));

        // Append files
        additionalFiles.forEach(file => {
          formDataObj.append('additionalFiles', file);
        });

        const response = await fetch('http://localhost:5000/api/uad36/export-pdf-with-files', {
          method: 'POST',
          body: formDataObj
        });

        if (!response.ok) throw new Error('Failed to generate PDF with files');

        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `UAD_3.6_Report_${formData.propertyAddress?.replace(/[^a-z0-9]/gi, '_') || Date.now()}.pdf`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);

        console.log(`✅ PDF downloaded successfully with ${additionalFiles.length} additional file(s)`);
      } else {
        // Use existing JSON endpoint for standard PDF
        const response = await apiService.post('/uad36/export-pdf', {
          formData,
          subjectProperty,
          comparables: selectedComparables,
          marketData,
          analysisResult
        }, {
          responseType: 'blob'
        });

        // Create blob and download
        const blob = new Blob([response.data], { type: 'application/pdf' });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `UAD_3.6_Report_${formData.propertyAddress?.replace(/[^a-z0-9]/gi, '_') || Date.now()}.pdf`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);

        console.log('✅ PDF downloaded successfully');
      }
    } catch (error) {
      console.error('❌ PDF generation error:', error);
      setPdfError(error.response?.data?.details || error.message || 'Failed to generate PDF');
    } finally {
      setIsGeneratingPDF(false);
    }
  };

  // Collapsible Section Component
  const Section = ({ title, sectionKey, badge = null, children }) => (
    <div className="border-2 border-gray-800 mb-4 rounded-lg overflow-hidden">
      <div
        className="bg-gradient-to-r from-blue-600 to-blue-700 p-4 cursor-pointer hover:from-blue-700 hover:to-blue-800 transition-all"
        onClick={() => toggleSection(sectionKey)}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span className="text-white text-xl">
              {expandedSections[sectionKey] ? '▼' : '▶'}
            </span>
            <h3 className="font-bold text-white text-lg">{title}</h3>
            {badge && <span className="bg-yellow-400 text-blue-900 px-3 py-1 rounded-full text-sm font-semibold">{badge}</span>}
          </div>
          <span className="text-blue-200 text-sm">
            {expandedSections[sectionKey] ? 'Click to collapse' : 'Click to expand'}
          </span>
        </div>
      </div>
      {expandedSections[sectionKey] && (
        <div className="p-4 bg-gray-50">
          {children}
        </div>
      )}
    </div>
  );

  const Field = ({ label, field, type = 'text', options = null, width = 'full', helpText = '' }) => (
    <div className={`mb-3 ${width === 'half' ? 'w-1/2 pr-2' : width === 'third' ? 'w-1/3 pr-2' : 'w-full'}`}>
      <label className="block text-xs font-medium text-gray-700 mb-1">
        {label}
        {helpText && <span className="text-xs text-gray-500 ml-1">({helpText})</span>}
      </label>
      {options ? (
        <select
          value={formData[field] || ''}
          onChange={(e) => handleChange(field, e.target.value)}
          className="w-full px-2 py-1 text-sm text-gray-900 bg-white border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
        >
          {options.map(opt => (
            <option key={opt} value={opt}>{opt}</option>
          ))}
        </select>
      ) : (
        <input
          type={type}
          value={formData[field] || ''}
          onChange={(e) => handleChange(field, e.target.value)}
          className="w-full px-2 py-1 text-sm text-gray-900 bg-white border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
        />
      )}
    </div>
  );

  return (
    <div className="bg-white p-6 rounded-lg shadow-lg">
      <h2 className="text-2xl font-bold mb-4 text-center">UNIFORM RESIDENTIAL APPRAISAL REPORT (UAD 3.6.0)</h2>
      <p className="text-center text-sm text-gray-600 mb-4">MISMO 3.6 | GSE UAD 3.6.0 v1.3 Compliant</p>

      {/* SUBJECT */}
      <Section title="SUBJECT PROPERTY" sectionKey="subject">
        <div className="grid grid-cols-2 gap-2">
          <Field label="Property Address" field="propertyAddress" />
          <div className="flex gap-2">
            <Field label="City" field="city" width="half" />
            <Field label="State" field="state" width="third" />
            <Field label="Zip" field="zip" width="third" />
          </div>
        </div>
        <div className="grid grid-cols-2 gap-2">
          <Field label="Legal Description" field="legalDescription" />
          <Field label="Parcel Identification Number (APN)" field="apn" />
        </div>
        <div className="grid grid-cols-4 gap-2">
          <Field label="Tax Year" field="taxYear" type="number" />
          <Field label="R.E. Taxes $" field="reTaxes" type="number" />
          <Field label="Neighborhood Name" field="neighborhoodName" />
          <Field label="Map Reference" field="mapReference" />
        </div>
        <div className="grid grid-cols-4 gap-2">
          <Field label="Census Tract" field="censusTract" />
          <Field label="Occupant" field="occupant" options={['Owner', 'Tenant', 'Vacant']} />
          <Field label="Special Assessments" field="specialAssessments" />
          <div className="flex gap-2">
            <Field label="PUD" field="pud" options={['Yes', 'No']} width="half" />
            <Field label="HOA" field="hoa" options={['Yes', 'No']} width="half" />
          </div>
        </div>
        {formData.hoa === 'Yes' && (
          <Field label="HOA $ per month" field="hoaPerMonth" type="number" />
        )}
      </Section>

      {/* CONTRACT */}
      <Section title="CONTRACT" sectionKey="subject">
        <div className="grid grid-cols-3 gap-2">
          <Field label="Sale Price $" field="salePrice" type="number" />
          <Field label="Date of Sale" field="dateOfSale" type="date" />
          <Field label="Concessions" field="concessions" />
        </div>
        <div className="grid grid-cols-2 gap-2">
          <Field label="Property Rights (UAD 3.6)" field="propertyRights"
            options={['FeeSimple', 'Leasehold', 'Other']}
            helpText="No spaces" />
          <Field label="Assignment Type" field="assignmentType" options={['Purchase', 'Refinance', 'Other']} />
        </div>
      </Section>

      {/* NEIGHBORHOOD */}
      <Section title="NEIGHBORHOOD & MARKET" sectionKey="market" badge="UAD 3.6">
        <div className="grid grid-cols-3 gap-2">
          <Field label="Location Type (UAD)" field="locationType" options={['Urban', 'Suburban', 'Rural']} />
          <Field label="Built-Up" field="builtUp" options={['Over 75%', '25-75%', 'Under 25%']} />
          <Field label="Growth" field="growth" options={['Rapid', 'Stable', 'Slow']} />
        </div>
        <div className="grid grid-cols-3 gap-2">
          <Field label="Property Values" field="propertyValues" options={['Increasing', 'Stable', 'Declining']} />
          <Field label="Market Trend (UAD 3.6)" field="marketTrend" options={['Increasing', 'Stable', 'Declining']} />
          <Field label="Marketing Time" field="marketingTime" options={['Under 3 months', '3-6 months', 'Over 6 months']} />
        </div>
        <Field label="Neighborhood Boundaries" field="neighborhoodBoundaries" />
        <Field label="Neighborhood Description" field="neighborhoodDescription" />
        <Field label="Market Conditions" field="marketConditions" />
      </Section>

      {/* SITE (UAD 3.6 Enhanced) */}
      <Section title="SITE" sectionKey="site" badge="UAD 3.6">
        <div className="grid grid-cols-4 gap-2">
          <Field label="Dimensions" field="siteDimensions" />
          <Field label="Area (sq ft)" field="siteArea" type="number" />
          <Field label="Shape Type" field="siteShape"
            options={['Rectangular', 'Irregular', 'Triangular', 'Other']} />
          <Field label="Topography" field="siteTopography"
            options={['Level', 'Rolling', 'Hilly', 'Steep', 'Other']} />
        </div>
        <div className="grid grid-cols-2 gap-2">
          <Field label="View Type (UAD 3.6)" field="viewType"
            options={['Residential', 'Commercial', 'Industrial', 'Water', 'Mountain', 'Golf', 'Park', 'Other']} />
          <Field label="Zoning Classification" field="zoningClassification" />
        </div>
        <Field label="Zoning Description" field="zoningDescription" />
        <Field label="Zoning Compliance" field="zoningCompliance" options={['Legal', 'Legal Nonconforming', 'Illegal']} />

        <div className="mt-3">
          <p className="font-semibold mb-2">Utilities (UAD 3.6):</p>
          <div className="grid grid-cols-4 gap-2">
            <Field label="Electricity" field="utilityElectricity" options={['Public', 'Other']} />
            <Field label="Gas" field="utilityGas" options={['Public', 'Other']} />
            <Field label="Water Source" field="waterSource"
              options={['PublicSupply', 'CommunitySupply', 'Well', 'Cistern', 'Lake', 'Spring', 'Other']} />
            <Field label="Sewer System" field="sewerSystem"
              options={['PublicSewer', 'CommunitySewer', 'Septic', 'Cesspool', 'Other']} />
          </div>
        </div>

        <div className="mt-3">
          <p className="font-semibold mb-2">FEMA Flood Hazard:</p>
          <div className="grid grid-cols-4 gap-2">
            <Field label="Special Flood Hazard" field="femaFloodHazard" options={['Yes', 'No']} />
            <Field label="FEMA Zone" field="femaZone" />
            <Field label="FEMA Map #" field="femaMapNumber" />
            <Field label="FEMA Map Date" field="femaMapDate" type="date" />
          </div>
        </div>
      </Section>

      {/* IMPROVEMENTS (UAD 3.6 Enhanced) */}
      <Section title="IMPROVEMENTS" sectionKey="improvements" badge="UAD 3.6">

        <div className="grid grid-cols-4 gap-2">
          <Field label="Unit Count" field="unitCount" type="number" />
          <Field label="Level Count" field="levelCount" type="number" />
          <Field label="Attachment Type" field="attachmentType"
            options={['Detached', 'Attached', 'SemiDetached']}
            helpText="No spaces" />
          <Field label="Design (Style)" field="design"
            options={['Ranch', 'Colonial', 'SplitLevel', 'BiLevel', 'Contemporary', 'Traditional', 'Other']} />
        </div>

        <div className="grid grid-cols-4 gap-2">
          <Field label="Year Built" field="yearBuilt" type="number" />
          <Field label="Effective Age" field="effectiveAge" type="number" />
          <Field label="Construction Method" field="constructionMethod"
            options={['SiteBuilt', 'Manufactured', 'Modular', 'OnFrameModular', 'Container', 'ThreeDimensionalPrintingTechnology', 'Other']}
            helpText="UAD enum" />
          <Field label="Construction Status" field="constructionStatus"
            options={['Complete', 'Incomplete']} />
        </div>

        <div className="grid grid-cols-3 gap-2">
          <Field label="Property Estate Type" field="propertyEstate"
            options={['FeeSimple', 'Leasehold', 'Other']}
            helpText="No spaces" />
          <Field label="Property Sub Type" field="propertySubType"
            options={['SingleFamilyResidence', 'Condominium', 'Townhouse', 'PUD', 'CoOperative', 'Other']} />
          <Field label="Current Usage" field="currentUsage"
            options={['SingleFamilyResidence', 'TwoToFourUnitProperty', 'Condominium', 'Other']} />
        </div>

        <div className="grid grid-cols-4 gap-2">
          <Field label="Foundation Type" field="foundationType"
            options={['Slab', 'CrawlSpace', 'Basement', 'Combination']} />
          <Field label="Exterior Material" field="exteriorMaterial"
            options={['Brick', 'Vinyl', 'Wood', 'Stucco', 'Stone', 'Aluminum', 'Cement', 'HardBoard', 'MetalSiding', 'Other']} />
          <Field label="Roof Material" field="roofMaterial"
            options={['Composition', 'Asphalt', 'Wood', 'Tile', 'Slate', 'Metal', 'Other']} />
          <Field label="Window Type" field="windowType"
            options={['Aluminum', 'Vinyl', 'Wood', 'Clad']} />
        </div>

        {/* AI Image Analysis Section */}
        <div className="mt-4 bg-gradient-to-r from-purple-50 to-blue-50 p-4 rounded-lg border-2 border-purple-200">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <ImageIcon className="w-5 h-5 text-purple-600" />
              <h3 className="font-bold text-purple-800">AI-Powered Property Analysis</h3>
            </div>
            <span className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded-full font-medium">
              ⚡ Auto-runs with PDF
            </span>
            {analysisResult && (
              <span className="flex items-center gap-1 text-green-600 text-sm">
                <CheckCircle className="w-4 h-4" />
                Analysis Complete
              </span>
            )}
          </div>

          <p className="text-sm text-gray-600 mb-3">
            <strong>Note:</strong> AI analysis runs automatically when generating PDF. You can also trigger it manually here to preview ratings ({propertyImages.length} images, ~60-90 seconds).
          </p>

          <button
            onClick={analyzePropertyImages}
            disabled={isAnalyzingImages || propertyImages.length === 0}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold transition-all ${
              isAnalyzingImages || propertyImages.length === 0
                ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                : 'bg-gradient-to-r from-purple-600 to-blue-600 text-white hover:from-purple-700 hover:to-blue-700 shadow-lg'
            }`}
          >
            {isAnalyzingImages ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin" />
                Analyzing {propertyImages.length} Images...
              </>
            ) : (
              <>
                <Wand2 className="w-5 h-5" />
                Analyze Property Images ({propertyImages.length})
              </>
            )}
          </button>

          {analysisError && (
            <div className="mt-3 flex items-center gap-2 text-red-600 text-sm bg-red-50 p-2 rounded">
              <XCircle className="w-4 h-4" />
              {analysisError}
            </div>
          )}

          {analysisResult && (
            <div className="mt-3 text-sm bg-white p-3 rounded border">
              <p className="font-semibold text-gray-700 mb-2">Analysis Results:</p>
              <div className="grid grid-cols-2 gap-2 text-xs">
                <div><span className="font-semibold">Images Analyzed:</span> {analysisResult.totalImagesAnalyzed}</div>
                <div><span className="font-semibold">Exterior Images:</span> {analysisResult.exteriorAnalyses?.length || 0}</div>
                <div><span className="font-semibold">Interior Images:</span> {analysisResult.interiorAnalyses?.length || 0}</div>
                <div><span className="font-semibold">Confidence:</span> High</div>
              </div>
            </div>
          )}
        </div>

        {/* UAD 3.6 Quality Ratings */}
        <div className="mt-4 bg-yellow-50 p-3 rounded">
          <p className="font-semibold mb-2 text-blue-800">UAD 3.6 Quality Ratings (Q1=Best, Q6=Poor):</p>
          <div className="grid grid-cols-3 gap-2">
            <Field label="Overall Quality" field="overallQuality"
              options={['Q1', 'Q2', 'Q3', 'Q4', 'Q5', 'Q6']}
              helpText="Q1-Q6" />
            <Field label="Interior Quality" field="interiorQuality"
              options={['Q1', 'Q2', 'Q3', 'Q4', 'Q5', 'Q6']} />
            <Field label="Exterior Quality" field="exteriorQuality"
              options={['Q1', 'Q2', 'Q3', 'Q4', 'Q5', 'Q6']} />
          </div>
        </div>

        {/* UAD 3.6 Condition Ratings */}
        <div className="mt-4 bg-green-50 p-3 rounded">
          <p className="font-semibold mb-2 text-blue-800">UAD 3.6 Condition Ratings (C1=Excellent, C6=Poor):</p>
          <div className="grid grid-cols-3 gap-2">
            <Field label="Overall Condition" field="overallCondition"
              options={['C1', 'C2', 'C3', 'C4', 'C5', 'C6']}
              helpText="C1-C6" />
            <Field label="Interior Condition" field="interiorCondition"
              options={['C1', 'C2', 'C3', 'C4', 'C5', 'C6']} />
            <Field label="Exterior Condition" field="exteriorCondition"
              options={['C1', 'C2', 'C3', 'C4', 'C5', 'C6']} />
          </div>
        </div>

        {/* Property Images Gallery */}
        {propertyImages.length > 0 && (
          <div className="mt-4 bg-gray-50 p-4 rounded-lg border border-gray-300">
            <h3 className="font-bold text-gray-800 mb-3">Property Images ({propertyImages.length})</h3>
            <div className="grid grid-cols-4 gap-3 max-h-96 overflow-y-auto">
              {propertyImages.slice(0, 20).map((imageUrl, index) => (
                <div key={index} className="relative group">
                  <img
                    src={imageUrl}
                    alt={`Property ${index + 1}`}
                    className="w-full h-32 object-cover rounded border border-gray-300 hover:border-blue-500 transition-all cursor-pointer"
                    onClick={() => window.open(imageUrl, '_blank')}
                  />
                  <div className="absolute bottom-0 left-0 right-0 bg-black bg-opacity-50 text-white text-xs p-1 text-center opacity-0 group-hover:opacity-100 transition-opacity">
                    Image {index + 1}
                  </div>
                </div>
              ))}
            </div>
            {propertyImages.length > 20 && (
              <p className="text-sm text-gray-600 mt-2">
                Showing first 20 of {propertyImages.length} images (AI analyzes up to 20)
              </p>
            )}
          </div>
        )}

        <div className="mt-4">
          <p className="font-semibold mb-2">Basement:</p>
          <div className="grid grid-cols-5 gap-2">
            <Field label="Area (sq ft)" field="basementArea" type="number" />
            <Field label="% Finished" field="basementFinishPercent" type="number" />
            <Field label="Outside Entry" field="basementOutsideEntry" options={['Yes', 'No']} />
            <Field label="Rec Room" field="basementRecRoom" type="number" />
            <Field label="Bedroom #" field="basementBedroom" type="number" />
          </div>
        </div>

        <div className="mt-4">
          <p className="font-semibold mb-2">Insulation:</p>
          <div className="grid grid-cols-4 gap-2">
            <Field label="Roof" field="insulationRoof" options={['Yes', 'No', 'Unknown']} />
            <Field label="Ceiling" field="insulationCeiling" options={['Yes', 'No', 'Unknown']} />
            <Field label="Walls" field="insulationWalls" options={['Yes', 'No', 'Unknown']} />
            <Field label="Floor" field="insulationFloor" options={['Yes', 'No', 'Unknown']} />
          </div>
        </div>

        <div className="mt-4">
          <p className="font-semibold mb-2">Heating/Cooling (UAD 3.6):</p>
          <div className="grid grid-cols-4 gap-2">
            <Field label="Heating System" field="heatingSystem"
              options={['ForcedWarmAir', 'HotWater', 'HeatPump', 'Radiant', 'Electric', 'Other']} />
            <Field label="Heating Fuel" field="heatingFuelType"
              options={['NaturalGas', 'Electric', 'Oil', 'Propane', 'Wood', 'Coal', 'Solar', 'Other']} />
            <Field label="Cooling System" field="coolingSystem"
              options={['Central', 'Wall', 'Window', 'Evaporative', 'None']} />
          </div>
        </div>

        <div className="mt-4">
          <p className="font-semibold mb-2">Amenities:</p>
          <div className="grid grid-cols-5 gap-2">
            <Field label="Fireplace(s) #" field="fireplaces" type="number" />
            <Field label="Patio/Deck" field="patioDeck" options={['Yes', 'No']} />
            <Field label="Pool" field="pool" options={['Yes', 'No']} />
            <Field label="Fence" field="fence" options={['Wood', 'Chain Link', 'None', 'Other']} />
            <Field label="Porch" field="porch" options={['Yes', 'No']} />
          </div>
        </div>

        <div className="mt-4">
          <p className="font-semibold mb-2">Car Storage (UAD 3.6):</p>
          <div className="grid grid-cols-3 gap-2">
            <Field label="Car Storage Type" field="carStorageTypeUAD"
              options={['None', 'Driveway', 'AttachedGarage', 'DetachedGarage', 'BuiltInGarage', 'Carport', 'Other']}
              helpText="UAD enum" />
            <Field label="# of Spaces" field="garageSpaces" type="number" />
          </div>
        </div>
      </Section>

      {/* ROOM LIST (UAD 3.6 Enhanced) */}
      <Section title="ROOMS & GLA" sectionKey="rooms" badge="UAD 3.6">

        <div className="mb-3">
          <p className="font-semibold mb-2">Level 1:</p>
          <div className="grid grid-cols-4 gap-2">
            <Field label="Foyer" field="foyer" type="number" />
            <Field label="Living Room" field="livingRoom" type="number" />
            <Field label="Dining Room" field="diningRoom" type="number" />
            <Field label="Kitchen" field="kitchen" type="number" />
          </div>
          <div className="grid grid-cols-4 gap-2">
            <Field label="Den" field="den" type="number" />
            <Field label="Family Room" field="familyRoom" type="number" />
            <Field label="Bedroom" field="level1Bedrooms" type="number" />
            <Field label="Bath" field="level1Baths" type="number" />
          </div>
        </div>

        <div className="mb-3">
          <p className="font-semibold mb-2">Level 2:</p>
          <div className="grid grid-cols-3 gap-2">
            <Field label="Bedroom" field="level2Bedrooms" type="number" />
            <Field label="Bath" field="level2Baths" type="number" />
          </div>
        </div>

        <div className="bg-blue-50 p-3 rounded">
          <p className="font-semibold mb-2">Totals (UAD 3.6):</p>
          <div className="grid grid-cols-4 gap-2">
            <Field label="Gross Living Area (GLA)" field="gla" type="number" helpText="sq ft" />
            <Field label="Total Bedrooms" field="totalBedrooms" type="number" />
            <Field label="Full Bathrooms" field="fullBathrooms" type="number" helpText="separate count" />
            <Field label="Half Bathrooms" field="halfBathrooms" type="number" helpText="separate count" />
          </div>
        </div>
      </Section>

      {/* INTERIOR */}
      <Section title="INTERIOR & AMENITIES" sectionKey="amenities">
        <div className="grid grid-cols-3 gap-2">
          <Field label="Floors" field="floors" options={['Hardwood', 'Carpet', 'Tile', 'Vinyl', 'Concrete', 'Other']} />
          <Field label="Walls" field="walls" options={['Drywall', 'Plaster', 'Wood', 'Other']} />
          <Field label="Trim/Finish" field="trim" options={['Wood', 'Vinyl', 'Other']} />
        </div>
        <div className="grid grid-cols-3 gap-2">
          <Field label="Bath Floor" field="bathFloor" options={['Ceramic', 'Vinyl', 'Tile', 'Other']} />
          <Field label="Bath Wainscot" field="bathWainscot" options={['Ceramic', 'Plastic', 'Tile', 'Other']} />
          <Field label="Doors" field="doors" options={['Wood', 'Hollow Core', 'Solid Core', 'Metal']} />
        </div>
        <div className="grid grid-cols-2 gap-2">
          <Field label="Attic" field="attic" options={['None', 'Stairs', 'Drop Stair', 'Scuttle', 'Floor', 'Heated', 'Finished']} />
          <Field label="Appliances" field="appliances" />
        </div>
      </Section>

      {/* COMPARABLE SALES - DYNAMIC */}
      <Section
        title="COMPARABLE SALES"
        sectionKey="comparables"
        badge={`${selectedComparables?.length || 0} Selected`}
      >
        {selectedComparables && selectedComparables.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full border-collapse text-sm">
              <thead>
                <tr className="bg-gradient-to-r from-blue-600 to-blue-700 text-white">
                  <th className="border border-gray-300 p-3 text-left sticky left-0 bg-blue-700">Field</th>
                  <th className="border border-gray-300 p-3">Subject</th>
                  {selectedComparables.map((comp, idx) => (
                    <th key={idx} className="border border-gray-300 p-3">Comp #{idx + 1}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="bg-white text-gray-900">
                <tr className="hover:bg-blue-50">
                  <td className="border border-gray-300 p-2 font-semibold bg-gray-200 sticky left-0 text-gray-900">Address</td>
                  <td className="border border-gray-300 p-2 text-gray-900">{subjectProperty?.StreetAddress || formData.propertyAddress}</td>
                  {selectedComparables.map((comp, idx) => {
                    const raw = comp.rawData || comp;
                    return <td key={idx} className="border border-gray-300 p-2 text-gray-900">{raw.UnparsedAddress || raw.Address || comp.address || '-'}</td>;
                  })}
                </tr>
                <tr className="hover:bg-blue-50">
                  <td className="border border-gray-300 p-2 font-semibold bg-gray-200 sticky left-0 text-gray-900">Proximity</td>
                  <td className="border border-gray-300 p-2 text-gray-900">-</td>
                  {selectedComparables.map((comp, idx) => {
                    const distance = comp.distance || (comp.rawData?.Distance);
                    return <td key={idx} className="border border-gray-300 p-2 text-gray-900">{distance ? `${parseFloat(distance).toFixed(2)} mi` : '-'}</td>;
                  })}
                </tr>
                <tr className="hover:bg-blue-50">
                  <td className="border border-gray-300 p-2 font-semibold bg-gray-200 sticky left-0 text-gray-900">Sale Price</td>
                  <td className="border border-gray-300 p-2 font-bold text-green-700">${(subjectProperty?.ListPrice || formData.salePrice || 0).toLocaleString()}</td>
                  {selectedComparables.map((comp, idx) => {
                    const raw = comp.rawData || comp;
                    const price = raw.ClosePrice || raw.ListPrice || 0;
                    return <td key={idx} className="border border-gray-300 p-2 font-bold text-blue-700">${price.toLocaleString()}</td>;
                  })}
                </tr>
                <tr className="hover:bg-blue-50">
                  <td className="border border-gray-300 p-2 font-semibold bg-gray-200 sticky left-0 text-gray-900">Sale Date</td>
                  <td className="border border-gray-300 p-2 text-gray-900">{formData.dateOfSale || '-'}</td>
                  {selectedComparables.map((comp, idx) => {
                    const raw = comp.rawData || comp;
                    const date = raw.CloseDate || raw.ListingContractDate || comp.date || '-';
                    return <td key={idx} className="border border-gray-300 p-2 text-gray-900">{date}</td>;
                  })}
                </tr>
                <tr className="hover:bg-blue-50">
                  <td className="border border-gray-300 p-2 font-semibold bg-gray-200 sticky left-0 text-gray-900">Data Source / MLS#</td>
                  <td className="border border-gray-300 p-2 text-gray-900">-</td>
                  {selectedComparables.map((comp, idx) => {
                    const raw = comp.rawData || comp;
                    const source = raw.DataSource || raw.MlsBoard || 'MLS';
                    const mlsNum = raw.MlsListingNumber || raw.MLSNumber || comp.mlsNumber || '';
                    return <td key={idx} className="border border-gray-300 p-2 text-gray-900">{source}{mlsNum ? ` / ${mlsNum}` : ''}</td>;
                  })}
                </tr>
                <tr className="hover:bg-blue-50">
                  <td className="border border-gray-300 p-2 font-semibold bg-gray-200 sticky left-0 text-gray-900">Site/View</td>
                  <td className="border border-gray-300 p-2 text-gray-900">{formData.view || '-'}</td>
                  {selectedComparables.map((comp, idx) => {
                    const raw = comp.rawData || comp;
                    return <td key={idx} className="border border-gray-300 p-2 text-gray-900">{raw.View || raw.ViewFactors || '-'}</td>;
                  })}
                </tr>
                <tr className="hover:bg-blue-50">
                  <td className="border border-gray-300 p-2 font-semibold bg-gray-200 sticky left-0 text-gray-900">Design/Style</td>
                  <td className="border border-gray-300 p-2 text-gray-900">{formData.design || subjectProperty?.ArchitecturalStyle || '-'}</td>
                  {selectedComparables.map((comp, idx) => {
                    const raw = comp.rawData || comp;
                    return <td key={idx} className="border border-gray-300 p-2 text-gray-900">{raw.ArchitecturalStyle || raw.PropertySubType || raw.PropertyType || '-'}</td>;
                  })}
                </tr>
                <tr className="hover:bg-blue-50">
                  <td className="border border-gray-300 p-2 font-semibold bg-gray-200 sticky left-0 text-gray-900">Quality</td>
                  <td className="border border-gray-300 p-2 text-gray-900">{formData.overallQuality || 'Q4'}</td>
                  {selectedComparables.map((comp, idx) => (
                    <td key={idx} className="border border-gray-300 p-2 text-gray-900">Q4</td>
                  ))}
                </tr>
                <tr className="hover:bg-blue-50">
                  <td className="border border-gray-300 p-2 font-semibold bg-gray-200 sticky left-0 text-gray-900">Condition</td>
                  <td className="border border-gray-300 p-2 text-gray-900">{formData.overallCondition || 'C4'}</td>
                  {selectedComparables.map((comp, idx) => (
                    <td key={idx} className="border border-gray-300 p-2 text-gray-900">C4</td>
                  ))}
                </tr>
                <tr className="hover:bg-blue-50">
                  <td className="border border-gray-300 p-2 font-semibold bg-gray-200 sticky left-0 text-gray-900">Year Built</td>
                  <td className="border border-gray-300 p-2 text-gray-900">{subjectProperty?.YearBuilt || formData.yearBuilt || '-'}</td>
                  {selectedComparables.map((comp, idx) => {
                    const raw = comp.rawData || comp;
                    return <td key={idx} className="border border-gray-300 p-2 text-gray-900">{raw.YearBuilt || '-'}</td>;
                  })}
                </tr>
                <tr className="hover:bg-blue-50">
                  <td className="border border-gray-300 p-2 font-semibold bg-gray-200 sticky left-0 text-gray-900">Actual Age</td>
                  <td className="border border-gray-300 p-2 text-gray-900">{subjectProperty?.YearBuilt ? (new Date().getFullYear() - parseInt(subjectProperty.YearBuilt)) : '-'} yrs</td>
                  {selectedComparables.map((comp, idx) => {
                    const raw = comp.rawData || comp;
                    const age = raw.YearBuilt ? (new Date().getFullYear() - parseInt(raw.YearBuilt)) : null;
                    return <td key={idx} className="border border-gray-300 p-2 text-gray-900">{age ? `${age} yrs` : '-'}</td>;
                  })}
                </tr>
                <tr className="hover:bg-blue-50">
                  <td className="border border-gray-300 p-2 font-semibold bg-gray-200 sticky left-0 text-gray-900">Above Grade Rooms</td>
                  <td className="border border-gray-300 p-2 text-gray-900">{subjectProperty?.RoomsTotal || formData.totalRooms || 0}</td>
                  {selectedComparables.map((comp, idx) => {
                    const raw = comp.rawData || comp;
                    return <td key={idx} className="border border-gray-300 p-2 text-gray-900">{raw.RoomsTotal || '-'}</td>;
                  })}
                </tr>
                <tr className="hover:bg-blue-50">
                  <td className="border border-gray-300 p-2 font-semibold bg-gray-200 sticky left-0 text-gray-900">Bedrooms</td>
                  <td className="border border-gray-300 p-2 text-gray-900">{subjectProperty?.BedroomsTotal || formData.totalBedrooms || 0}</td>
                  {selectedComparables.map((comp, idx) => {
                    const raw = comp.rawData || comp;
                    return <td key={idx} className="border border-gray-300 p-2 text-gray-900">{raw.BedroomsTotal || comp.beds || 0}</td>;
                  })}
                </tr>
                <tr className="hover:bg-blue-50">
                  <td className="border border-gray-300 p-2 font-semibold bg-gray-200 sticky left-0 text-gray-900">Bathrooms</td>
                  <td className="border border-gray-300 p-2 text-gray-900">{subjectProperty?.BathroomsTotalInteger || formData.fullBathrooms || 0}</td>
                  {selectedComparables.map((comp, idx) => {
                    const raw = comp.rawData || comp;
                    return <td key={idx} className="border border-gray-300 p-2 text-gray-900">{raw.BathroomsTotalInteger || raw.BathroomsDecimal || comp.baths || 0}</td>;
                  })}
                </tr>
                <tr className="hover:bg-blue-50">
                  <td className="border border-gray-300 p-2 font-semibold bg-gray-200 sticky left-0 text-gray-900">GLA (sq ft)</td>
                  <td className="border border-gray-300 p-2 text-gray-900">{(subjectProperty?.LivingArea || formData.gla || 0).toLocaleString()}</td>
                  {selectedComparables.map((comp, idx) => {
                    const raw = comp.rawData || comp;
                    const gla = raw.LivingArea || comp.livingArea || 0;
                    return <td key={idx} className="border border-gray-300 p-2 text-gray-900">{gla.toLocaleString()}</td>;
                  })}
                </tr>
                <tr className="hover:bg-blue-50">
                  <td className="border border-gray-300 p-2 font-semibold bg-gray-200 sticky left-0 text-gray-900">$/sq ft</td>
                  <td className="border border-gray-300 p-2 text-gray-900">${((subjectProperty?.ListPrice || formData.salePrice || 0) / (subjectProperty?.LivingArea || formData.gla || 1)).toFixed(2)}</td>
                  {selectedComparables.map((comp, idx) => {
                    const raw = comp.rawData || comp;
                    const price = raw.ClosePrice || raw.ListPrice || 0;
                    const area = raw.LivingArea || comp.livingArea || 1;
                    return <td key={idx} className="border border-gray-300 p-2 text-gray-900">${(price / area).toFixed(2)}</td>;
                  })}
                </tr>
                <tr className="hover:bg-blue-50">
                  <td className="border border-gray-300 p-2 font-semibold bg-gray-200 sticky left-0 text-gray-900">Basement & Finished</td>
                  <td className="border border-gray-300 p-2 text-gray-900">{formData.basementArea || 0} sf</td>
                  {selectedComparables.map((comp, idx) => {
                    const raw = comp.rawData || comp;
                    const basement = raw.BelowGradeTotalArea || raw.BasementArea || 0;
                    const finished = raw.BasementFinishedArea || 0;
                    return <td key={idx} className="border border-gray-300 p-2 text-gray-900">{basement > 0 ? `${basement} sf (${finished} fin)` : 'None'}</td>;
                  })}
                </tr>
                <tr className="hover:bg-blue-50">
                  <td className="border border-gray-300 p-2 font-semibold bg-gray-200 sticky left-0 text-gray-900">Functional Utility</td>
                  <td className="border border-gray-300 p-2 text-gray-900">Average</td>
                  {selectedComparables.map((comp, idx) => (
                    <td key={idx} className="border border-gray-300 p-2 text-gray-900">Average</td>
                  ))}
                </tr>
                <tr className="hover:bg-blue-50">
                  <td className="border border-gray-300 p-2 font-semibold bg-gray-200 sticky left-0 text-gray-900">Heating/Cooling</td>
                  <td className="border border-gray-300 p-2 text-gray-900">{subjectProperty?.Heating || formData.heating || '-'} / {subjectProperty?.Cooling || formData.cooling || '-'}</td>
                  {selectedComparables.map((comp, idx) => {
                    const raw = comp.rawData || comp;
                    return <td key={idx} className="border border-gray-300 p-2 text-gray-900">{raw.Heating || '-'} / {raw.Cooling || '-'}</td>;
                  })}
                </tr>
                <tr className="hover:bg-blue-50">
                  <td className="border border-gray-300 p-2 font-semibold bg-gray-200 sticky left-0 text-gray-900">Garage/Carport</td>
                  <td className="border border-gray-300 p-2 text-gray-900">{subjectProperty?.GarageSpaces || formData.garageSpaces || 0} car {subjectProperty?.CarStorageType || 'garage'}</td>
                  {selectedComparables.map((comp, idx) => {
                    const raw = comp.rawData || comp;
                    const spaces = raw.GarageSpaces || raw.ParkingSpaces || 0;
                    const type = raw.CarStorageType || raw.ParkingFeatures || 'garage';
                    return <td key={idx} className="border border-gray-300 p-2 text-gray-900">{spaces} car {type}</td>;
                  })}
                </tr>
                <tr className="hover:bg-blue-50">
                  <td className="border border-gray-300 p-2 font-semibold bg-gray-200 sticky left-0 text-gray-900">Porch/Patio/Deck</td>
                  <td className="border border-gray-300 p-2 text-gray-900">{subjectProperty?.PatioAndPorchFeatures || formData.patio || '-'}</td>
                  {selectedComparables.map((comp, idx) => {
                    const raw = comp.rawData || comp;
                    return <td key={idx} className="border border-gray-300 p-2 text-gray-900">{raw.PatioAndPorchFeatures || raw.DeckDescription || '-'}</td>;
                  })}
                </tr>
                <tr className="hover:bg-blue-50">
                  <td className="border border-gray-300 p-2 font-semibold bg-gray-200 sticky left-0 text-gray-900">Lot Size (sq ft)</td>
                  <td className="border border-gray-300 p-2 text-gray-900">{(subjectProperty?.LotSizeSquareFeet || formData.siteArea || 0).toLocaleString()}</td>
                  {selectedComparables.map((comp, idx) => {
                    const raw = comp.rawData || comp;
                    const lotSize = raw.LotSizeSquareFeet || comp.lotSize || 0;
                    return <td key={idx} className="border border-gray-300 p-2 text-gray-900">{lotSize ? parseInt(lotSize).toLocaleString() : '-'}</td>;
                  })}
                </tr>
                <tr className="hover:bg-blue-50">
                  <td className="border border-gray-300 p-2 font-semibold bg-gray-200 sticky left-0 text-gray-900">Pool</td>
                  <td className="border border-gray-300 p-2 text-gray-900">{subjectProperty?.PoolFeatures ? 'Yes' : 'No'}</td>
                  {selectedComparables.map((comp, idx) => {
                    const raw = comp.rawData || comp;
                    const hasPool = raw.PoolFeatures && raw.PoolFeatures !== '' && raw.PoolFeatures !== 'None';
                    return <td key={idx} className="border border-gray-300 p-2 text-gray-900">{hasPool ? 'Yes' : 'No'}</td>;
                  })}
                </tr>
                <tr className="hover:bg-blue-50">
                  <td className="border border-gray-300 p-2 font-semibold bg-gray-200 sticky left-0 text-gray-900">Fireplace(s)</td>
                  <td className="border border-gray-300 p-2 text-gray-900">{subjectProperty?.FireplacesTotal || 0}</td>
                  {selectedComparables.map((comp, idx) => {
                    const raw = comp.rawData || comp;
                    return <td key={idx} className="border border-gray-300 p-2 text-gray-900">{raw.FireplacesTotal || raw.FireplaceTotal || 0}</td>;
                  })}
                </tr>
                <tr className="hover:bg-blue-50">
                  <td className="border border-gray-300 p-2 font-semibold bg-gray-200 sticky left-0 text-gray-900">Stories</td>
                  <td className="border border-gray-300 p-2 text-gray-900">{subjectProperty?.StoriesTotal || formData.numStories || 1}</td>
                  {selectedComparables.map((comp, idx) => {
                    const raw = comp.rawData || comp;
                    return <td key={idx} className="border border-gray-300 p-2 text-gray-900">{raw.StoriesTotal || raw.DerivedStories || '-'}</td>;
                  })}
                </tr>
                <tr className="hover:bg-blue-50">
                  <td className="border border-gray-300 p-2 font-semibold bg-gray-200 sticky left-0 text-gray-900">MLS Status</td>
                  <td className="border border-gray-300 p-2 text-gray-900">{subjectProperty?.MlsStatus || subjectProperty?.StandardStatus || '-'}</td>
                  {selectedComparables.map((comp, idx) => {
                    const raw = comp.rawData || comp;
                    const status = raw.MlsStatus || raw.StandardStatus || comp.mlsStatus || '-';
                    return <td key={idx} className="border border-gray-300 p-2 text-gray-900">{status}</td>;
                  })}
                </tr>
                <tr className="hover:bg-blue-50">
                  <td className="border border-gray-300 p-2 font-semibold bg-gray-200 sticky left-0 text-gray-900">Days on Market</td>
                  <td className="border border-gray-300 p-2 text-gray-900">{subjectProperty?.DaysOnMarket || '-'}</td>
                  {selectedComparables.map((comp, idx) => {
                    const raw = comp.rawData || comp;
                    const dom = raw.DaysOnMarket || raw.CumulativeDaysOnMarket || '-';
                    return <td key={idx} className="border border-gray-300 p-2 text-gray-900">{dom ? `${dom} days` : '-'}</td>;
                  })}
                </tr>
                <tr className="hover:bg-blue-50">
                  <td className="border border-gray-300 p-2 font-semibold bg-gray-200 sticky left-0 text-gray-900">Property SubType</td>
                  <td className="border border-gray-300 p-2 text-gray-900">{subjectProperty?.PropertySubType || subjectProperty?.PropertyType || '-'}</td>
                  {selectedComparables.map((comp, idx) => {
                    const raw = comp.rawData || comp;
                    return <td key={idx} className="border border-gray-300 p-2 text-gray-900">{raw.PropertySubType || raw.PropertyType || '-'}</td>;
                  })}
                </tr>
                <tr className="hover:bg-blue-50">
                  <td className="border border-gray-300 p-2 font-semibold bg-gray-200 sticky left-0 text-gray-900">Roof Material</td>
                  <td className="border border-gray-300 p-2 text-gray-900">{subjectProperty?.RoofMaterial || formData.roof || '-'}</td>
                  {selectedComparables.map((comp, idx) => {
                    const raw = comp.rawData || comp;
                    return <td key={idx} className="border border-gray-300 p-2 text-gray-900">{raw.RoofMaterial || raw.Roof || '-'}</td>;
                  })}
                </tr>
                <tr className="hover:bg-blue-50">
                  <td className="border border-gray-300 p-2 font-semibold bg-gray-200 sticky left-0 text-gray-900">Foundation</td>
                  <td className="border border-gray-300 p-2 text-gray-900">{subjectProperty?.FoundationDetails || formData.foundation || '-'}</td>
                  {selectedComparables.map((comp, idx) => {
                    const raw = comp.rawData || comp;
                    return <td key={idx} className="border border-gray-300 p-2 text-gray-900">{raw.FoundationDetails || raw.Foundation || '-'}</td>;
                  })}
                </tr>
                <tr className="hover:bg-blue-50">
                  <td className="border border-gray-300 p-2 font-semibold bg-gray-200 sticky left-0 text-gray-900">Exterior</td>
                  <td className="border border-gray-300 p-2 text-gray-900">{subjectProperty?.ExteriorFeatures || subjectProperty?.ConstructionMaterials || formData.exterior || '-'}</td>
                  {selectedComparables.map((comp, idx) => {
                    const raw = comp.rawData || comp;
                    return <td key={idx} className="border border-gray-300 p-2 text-gray-900">{raw.ExteriorFeatures || raw.ConstructionMaterials || '-'}</td>;
                  })}
                </tr>
                <tr className="hover:bg-blue-50">
                  <td className="border border-gray-300 p-2 font-semibold bg-gray-200 sticky left-0 text-gray-900">Flooring</td>
                  <td className="border border-gray-300 p-2 text-gray-900">{subjectProperty?.Flooring || formData.flooring || '-'}</td>
                  {selectedComparables.map((comp, idx) => {
                    const raw = comp.rawData || comp;
                    return <td key={idx} className="border border-gray-300 p-2 text-gray-900">{raw.Flooring || '-'}</td>;
                  })}
                </tr>
                <tr className="hover:bg-blue-50">
                  <td className="border border-gray-300 p-2 font-semibold bg-gray-200 sticky left-0 text-gray-900">Appliances</td>
                  <td className="border border-gray-300 p-2 text-gray-900">{subjectProperty?.Appliances || formData.appliances || '-'}</td>
                  {selectedComparables.map((comp, idx) => {
                    const raw = comp.rawData || comp;
                    return <td key={idx} className="border border-gray-300 p-2 text-gray-900">{raw.Appliances || '-'}</td>;
                  })}
                </tr>
                <tr className="hover:bg-blue-50">
                  <td className="border border-gray-300 p-2 font-semibold bg-gray-200 sticky left-0 text-gray-900">Water Source</td>
                  <td className="border border-gray-300 p-2 text-gray-900">{subjectProperty?.WaterSource || formData.waterSource || '-'}</td>
                  {selectedComparables.map((comp, idx) => {
                    const raw = comp.rawData || comp;
                    return <td key={idx} className="border border-gray-300 p-2 text-gray-900">{raw.WaterSource || '-'}</td>;
                  })}
                </tr>
                <tr className="hover:bg-blue-50">
                  <td className="border border-gray-300 p-2 font-semibold bg-gray-200 sticky left-0 text-gray-900">Sewer/Septic</td>
                  <td className="border border-gray-300 p-2 text-gray-900">{subjectProperty?.Sewer || formData.sewer || '-'}</td>
                  {selectedComparables.map((comp, idx) => {
                    const raw = comp.rawData || comp;
                    return <td key={idx} className="border border-gray-300 p-2 text-gray-900">{raw.Sewer || '-'}</td>;
                  })}
                </tr>
                <tr className="hover:bg-blue-50">
                  <td className="border border-gray-300 p-2 font-semibold bg-gray-200 sticky left-0 text-gray-900">Zoning</td>
                  <td className="border border-gray-300 p-2 text-gray-900">{subjectProperty?.Zoning || formData.zoning || '-'}</td>
                  {selectedComparables.map((comp, idx) => {
                    const raw = comp.rawData || comp;
                    return <td key={idx} className="border border-gray-300 p-2 text-gray-900">{raw.Zoning || '-'}</td>;
                  })}
                </tr>
                <tr className="hover:bg-blue-50">
                  <td className="border border-gray-300 p-2 font-semibold bg-gray-200 sticky left-0 text-gray-900">HOA/Condo Fees</td>
                  <td className="border border-gray-300 p-2 text-gray-900">{subjectProperty?.AssociationFee ? `$${subjectProperty.AssociationFee}/mo` : '-'}</td>
                  {selectedComparables.map((comp, idx) => {
                    const raw = comp.rawData || comp;
                    const fee = raw.AssociationFee || raw.AssociationFeeAmount || 0;
                    return <td key={idx} className="border border-gray-300 p-2 text-gray-900">{fee ? `$${fee}/mo` : '-'}</td>;
                  })}
                </tr>
                <tr className="hover:bg-blue-50">
                  <td className="border border-gray-300 p-2 font-semibold bg-gray-200 sticky left-0 text-gray-900">Utilities</td>
                  <td className="border border-gray-300 p-2 text-gray-900">{subjectProperty?.Utilities || formData.utilities || '-'}</td>
                  {selectedComparables.map((comp, idx) => {
                    const raw = comp.rawData || comp;
                    return <td key={idx} className="border border-gray-300 p-2 text-gray-900">{raw.Utilities || '-'}</td>;
                  })}
                </tr>
                <tr className="hover:bg-blue-50">
                  <td className="border border-gray-300 p-2 font-semibold bg-gray-200 sticky left-0 text-gray-900">Special Features</td>
                  <td className="border border-gray-300 p-2 text-gray-900">{subjectProperty?.SpecialListingConditions || formData.specialFeatures || '-'}</td>
                  {selectedComparables.map((comp, idx) => {
                    const raw = comp.rawData || comp;
                    return <td key={idx} className="border border-gray-300 p-2 text-gray-900">{raw.SpecialListingConditions || raw.PropertyFeatures || '-'}</td>;
                  })}
                </tr>
              </tbody>
            </table>
          </div>
        ) : (
          <div className="text-center py-8">
            <p className="text-gray-500 italic text-lg">No comparables selected</p>
            <p className="text-gray-400 text-sm mt-2">Go to Side-by-Side Comparison to select comparables</p>
          </div>
        )}
      </Section>

      {/* STRUCTURE ANALYSIS (NEW - GSE UAD 3.6.0) */}
      <Section title="STRUCTURE ANALYSIS" sectionKey="structureAnalysis" badge="GSE UAD 3.6.0">
        <div className="bg-blue-50 p-3 rounded mb-3 border border-blue-200">
          <p className="text-sm font-semibold text-blue-900">Age & Economic Life Analysis</p>
        </div>
        <div className="grid grid-cols-4 gap-2">
          <Field label="Actual Age (years)" field="actualAge" type="number" helpText="Auto-calculated from Year Built" />
          <Field label="Effective Age (years)" field="effectiveAge" type="number" helpText="Condition-based age" />
          <Field label="Remaining Economic Life" field="remainingEconomicLife" type="number" helpText="Years of useful life left" />
          <Field label="Total Economic Life" field="totalEconomicLife" type="number" helpText="Typical: 65 years" />
        </div>

        <div className="bg-orange-50 p-3 rounded my-3 border border-orange-200">
          <p className="text-sm font-semibold text-orange-900">Depreciation Breakdown</p>
        </div>
        <div className="grid grid-cols-4 gap-2">
          <Field label="Total Depreciation $" field="depreciationAmount" type="number" />
          <Field label="Physical Depreciation $" field="physicalDepreciation" type="number" helpText="Wear & tear" />
          <Field label="Functional Depreciation $" field="functionalDepreciation" type="number" helpText="Design obsolescence" />
          <Field label="External Depreciation $" field="externalDepreciation" type="number" helpText="Market forces" />
        </div>
      </Section>

      {/* COMPARABLE ADJUSTMENTS GRID (NEW - GSE UAD 3.6.0) */}
      <Section title="COMPARABLE ADJUSTMENTS GRID" sectionKey="adjustments" badge="GSE UAD 3.6.0">
        <div className="bg-green-50 p-3 rounded mb-3 border border-green-200">
          <p className="text-sm font-semibold text-green-900">Enter adjustment amounts for each comparable ($ amounts - positive if comp is inferior, negative if superior)</p>
          <p className="text-xs text-green-700 mt-1">Auto-calculated adjustments: GLA ($50/sqft), Bedrooms ($5k), Bathrooms ($3k), Garage ($8k/space), Pool ($15k), Age ($500/year)</p>
        </div>

        {[1, 2, 3, 4, 5, 6].map(compNum => (
          <div key={compNum} className="border-2 border-gray-300 p-4 mb-4 rounded">
            <h4 className="font-bold text-lg mb-3 text-blue-700">Comparable #{compNum} Adjustments</h4>
            <div className="grid grid-cols-3 gap-2 mb-2">
              <Field label="Sales Concessions $" field={`comp${compNum}SalesConcessionAdj`} type="number" />
              <Field label="Financing Concessions $" field={`comp${compNum}FinancingAdj`} type="number" />
              <Field label="Date of Sale $" field={`comp${compNum}DateOfSaleAdj`} type="number" />
            </div>
            <div className="grid grid-cols-3 gap-2 mb-2">
              <Field label="Location $" field={`comp${compNum}LocationAdj`} type="number" />
              <Field label="Site/View $" field={`comp${compNum}SiteViewAdj`} type="number" />
              <Field label="Design/Style $" field={`comp${compNum}DesignStyleAdj`} type="number" />
            </div>
            <div className="grid grid-cols-3 gap-2 mb-2">
              <Field label="Quality $" field={`comp${compNum}QualityAdj`} type="number" />
              <Field label="Age/Condition $" field={`comp${compNum}AgeConditionAdj`} type="number" helpText="Auto: $500/year" />
              <Field label="GLA $" field={`comp${compNum}GLAAdj`} type="number" helpText="Auto: $50/sqft" />
            </div>
            <div className="grid grid-cols-4 gap-2 mb-2">
              <Field label="Bedroom $" field={`comp${compNum}BedroomAdj`} type="number" helpText="Auto: $5k each" />
              <Field label="Bathroom $" field={`comp${compNum}BathroomAdj`} type="number" helpText="Auto: $3k each" />
              <Field label="Garage $" field={`comp${compNum}GarageAdj`} type="number" helpText="Auto: $8k/space" />
              <Field label="Pool $" field={`comp${compNum}PoolAdj`} type="number" helpText="Auto: $15k" />
            </div>
            <div className="grid grid-cols-4 gap-2">
              <Field label="Patio/Deck $" field={`comp${compNum}PatioAdj`} type="number" />
              <Field label="Fireplace $" field={`comp${compNum}FireplaceAdj`} type="number" />
              <Field label="Basement $" field={`comp${compNum}BasementAdj`} type="number" />
              <Field label="Functional Utility $" field={`comp${compNum}FunctionalUtilityAdj`} type="number" />
            </div>
          </div>
        ))}
      </Section>

      {/* VALUATION (UAD 3.6) */}
      <Section title="VALUATION & APPROACHES" sectionKey="valuation" badge="UAD 3.6">
        <div className="grid grid-cols-2 gap-2">
          <Field label="Property Valuation Method" field="valuationMethod"
            options={['TraditionalAppraisal', 'DesktopAppraisal', 'ExteriorAppraisal', 'HybridAppraisal']}
            helpText="UAD 3.6 required" />
          <Field label="Appraisal Purpose" field="appraisalPurpose"
            options={['Purchase', 'Refinance', 'MarketValue', 'Other']} />
        </div>

        <div className="bg-purple-50 p-3 rounded my-3 border border-purple-200">
          <p className="text-sm font-semibold text-purple-900">Highest & Best Use</p>
        </div>
        <div className="grid grid-cols-2 gap-2">
          <Field label="As Improved" field="highestBestUseImproved"
            placeholder="Present use as single-family residence..." />
          <Field label="As Vacant" field="highestBestUseVacant"
            placeholder="Single-family residential development..." />
        </div>
        <Field label="Highest & Best Use Comments" field="highestBestUseComment" />

        <div className="bg-green-50 p-3 rounded my-3 border border-green-200">
          <p className="text-sm font-semibold text-green-900">Sales Comparison Approach</p>
        </div>
        <div className="grid grid-cols-2 gap-2">
          <Field label="Sales Comparison Value $" field="salesComparisonValue" type="number" />
          <Field label="# of Comparables Used" field="comparablesCount" type="number" />
        </div>
        <Field label="Sales Comparison Comments" field="salesComparisonComments"
          placeholder="The Sales Comparison Approach was given primary consideration..." />

        <div className="bg-yellow-50 p-3 rounded my-3 border border-yellow-200">
          <p className="text-sm font-semibold text-yellow-900">Cost Approach (Optional)</p>
        </div>
        <div className="flex items-center gap-2 mb-2">
          <input type="checkbox" id="includeCostApproach"
            onChange={(e) => handleChange('includeCostApproach', e.target.checked)} />
          <label htmlFor="includeCostApproach" className="text-sm font-semibold">Include Cost Approach</label>
        </div>
        <div className="grid grid-cols-3 gap-2">
          <Field label="Reproduction Cost $" field="reproductionCost" type="number" />
          <Field label="Land Value $" field="landValue" type="number" />
          <Field label="Site Improvements $" field="siteImprovements" type="number" />
        </div>
        <div className="grid grid-cols-2 gap-2">
          <Field label="Total Depreciation $" field="depreciationAmount" type="number" />
          <Field label="Cost Approach Value $" field="costApproachValue" type="number" />
        </div>

        <div className="bg-blue-50 p-3 rounded my-3 border border-blue-200">
          <p className="text-sm font-semibold text-blue-900">Income Approach (Optional)</p>
        </div>
        <div className="flex items-center gap-2 mb-2">
          <input type="checkbox" id="includeIncomeApproach"
            onChange={(e) => handleChange('includeIncomeApproach', e.target.checked)} />
          <label htmlFor="includeIncomeApproach" className="text-sm font-semibold">Include Income Approach</label>
        </div>
        <div className="grid grid-cols-3 gap-2">
          <Field label="Gross Monthly Rent $" field="grossRent" type="number" />
          <Field label="Gross Rent Multiplier" field="grossRentMultiplier" type="number" />
          <Field label="Income Approach Value $" field="incomeApproachValue" type="number" />
        </div>
      </Section>

      {/* RECONCILIATION */}
      <Section title="RECONCILIATION" sectionKey="reconciliation" badge="UAD 3.6">
        <div className="grid grid-cols-2 gap-2">
          <Field label="Indicated Value (Sales Comparison)" field="indicatedValueSCA" type="number" />
          <Field label="Indicated Value (Income)" field="indicatedValueIncome" type="number" />
        </div>
        <div className="grid grid-cols-2 gap-2">
          <Field label="Indicated Value (Cost)" field="indicatedValueCost" type="number" />
          <Field label="Final Opinion of Value" field="finalValue" type="number" />
        </div>
        <div className="grid grid-cols-2 gap-2">
          <Field label="Value Conclusion Type" field="valueConclusionType"
            options={['AsIs', 'ProspectiveUponCompletion', 'Retrospective']} />
          <Field label="Approaches Used" field="approachesUsed"
            options={['SalesComparison', 'Cost', 'Income', 'Multiple']} />
        </div>
        <Field label="Reconciliation Comments" field="reconciliationComments"
          placeholder="Based on the analysis of comparable sales and current market conditions..." />
        <div className="grid grid-cols-2 gap-2 mt-3">
          <Field label="Extraordinary Assumptions" field="extraordinaryAssumptions"
            placeholder="Any assumptions that affect value..." />
          <Field label="Hypothetical Conditions" field="hypotheticalConditions"
            placeholder="Any hypothetical conditions..." />
        </div>
      </Section>

      {/* APPRAISER CERTIFICATION */}
      <Section title="APPRAISER CERTIFICATION" sectionKey="appraiser" badge="UAD 3.6">
        <div className="grid grid-cols-2 gap-2">
          <Field label="Appraiser Name" field="appraiserName" />
          <Field label="State Certification #" field="stateCertification" />
        </div>
        <div className="grid grid-cols-3 gap-2">
          <Field label="State" field="appraiserState" />
          <Field label="Effective Date" field="effectiveDate" type="date" />
          <Field label="Report Date" field="reportDate" type="date" />
        </div>
        <div className="grid grid-cols-2 gap-2">
          <Field label="Inspection Date" field="inspectionDate" type="date" />
          <Field label="Signature Date" field="signatureDate" type="date" />
        </div>
        <Field label="Certification Statement" field="certificationStatement"
          placeholder="I certify that, to the best of my knowledge and belief..." />
      </Section>

      {/* PDF Generation Button */}
      <div className="mt-6 bg-gradient-to-r from-green-50 to-blue-50 p-6 rounded-lg border-2 border-green-300 shadow-lg">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <FileText className="w-6 h-6 text-green-600" />
            <div>
              <h3 className="font-bold text-green-800 text-lg">Generate UAD 3.6 Report (URAR Form 1004)</h3>
              <p className="text-sm text-gray-600">
                Professional PDF report matching Uniform Residential Appraisal Report format
              </p>
            </div>
          </div>
        </div>

        {/* File Upload Section */}
        <div className="mb-4 bg-white p-4 rounded-lg border-2 border-blue-200">
          <div className="flex items-center gap-2 mb-2">
            <FileText className="w-5 h-5 text-blue-600" />
            <h4 className="font-semibold text-blue-800">Optional: Attach Additional Documents</h4>
          </div>
          <p className="text-sm text-gray-600 mb-3">
            Upload PDF, Word documents, or images to append to the end of the report (max 10 files, 10MB each)
          </p>
          <input
            type="file"
            accept=".pdf,.doc,.docx,image/*"
            multiple
            onChange={(e) => setAdditionalFiles(Array.from(e.target.files))}
            className="w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
          />
          {additionalFiles.length > 0 && (
            <div className="mt-3">
              <p className="text-sm font-semibold text-gray-700 mb-2">Selected files ({additionalFiles.length}):</p>
              <ul className="text-xs text-gray-600 space-y-1">
                {additionalFiles.map((file, idx) => (
                  <li key={idx} className="flex items-center gap-2">
                    <span className="text-green-600">✓</span>
                    {file.name} ({(file.size / 1024).toFixed(1)} KB)
                  </li>
                ))}
              </ul>
              <button
                onClick={() => setAdditionalFiles([])}
                className="mt-2 text-xs text-red-600 hover:text-red-800 underline"
              >
                Clear all files
              </button>
            </div>
          )}
        </div>

        <div className="flex items-center gap-4">
          <button
            onClick={generatePDF}
            disabled={isGeneratingPDF || !formData.propertyAddress}
            className={`flex items-center gap-2 px-6 py-3 rounded-lg font-semibold transition-all shadow-md ${
              isGeneratingPDF || !formData.propertyAddress
                ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                : 'bg-gradient-to-r from-green-600 to-blue-600 text-white hover:from-green-700 hover:to-blue-700'
            }`}
          >
            {isGeneratingPDF ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin" />
                Generating PDF...
              </>
            ) : (
              <>
                <Download className="w-5 h-5" />
                Download UAD 3.6 PDF Report
              </>
            )}
          </button>

          {analysisResult && (
            <div className="flex items-center gap-2 text-purple-700 text-sm bg-purple-50 px-3 py-2 rounded">
              <CheckCircle className="w-4 h-4" />
              AI Vision Analysis Included
            </div>
          )}
        </div>

        {pdfError && (
          <div className="mt-3 flex items-center gap-2 text-red-600 text-sm bg-red-50 p-3 rounded">
            <XCircle className="w-4 h-4" />
            {pdfError}
          </div>
        )}

        {!formData.propertyAddress && (
          <p className="mt-3 text-sm text-amber-700 bg-amber-50 p-2 rounded">
            ⚠️ Please ensure property address is filled to generate report
          </p>
        )}
      </div>
    </div>
  );
};

export default UAD36Form;
